import { ApiService } from "./api.service";
import type { TProjectState, TProjectStateUsage } from "@/types/project-state";

export interface TProjectStateCreateData {
  name: string;
  color: string;
  group: TProjectState["group"];
  description?: string | null;
}

export interface TProjectStateUpdateData {
  name?: string;
  color?: string;
  group?: TProjectState["group"];
  description?: string | null;
  sequence?: number;
}

export class ProjectStateService extends ApiService {
  async list(slug: string): Promise<TProjectState[]> {
    return this.get(`/api/workspaces/${slug}/project-states/`);
  }

  /** The status options this caller may pick for one project (NG-2510).
   *
   *  The project-scoped sibling of {@link list}: same workspace-wide list, but
   *  gated on `project.edit` for `projectId` instead of `state_machine.read`, so
   *  a Project Lead who can save a status can load the options even when the
   *  workspace settings list 403s. */
  async listPickable(slug: string, projectId: string): Promise<TProjectState[]> {
    return this.get(`/api/workspaces/${slug}/projects/${projectId}/state-options/`);
  }

  async create(slug: string, data: TProjectStateCreateData): Promise<TProjectState> {
    return this.post(`/api/workspaces/${slug}/project-states/`, data);
  }

  async update(
    slug: string,
    stateId: string,
    data: TProjectStateUpdateData,
  ): Promise<TProjectState> {
    return this.patch(`/api/workspaces/${slug}/project-states/${stateId}/`, data);
  }

  async remove(slug: string, stateId: string): Promise<void> {
    return this.delete(`/api/workspaces/${slug}/project-states/${stateId}/`);
  }

  async markDefault(slug: string, stateId: string): Promise<TProjectState> {
    return this.post(`/api/workspaces/${slug}/project-states/${stateId}/mark-default/`, {});
  }

  async usage(slug: string, stateId: string): Promise<TProjectStateUsage> {
    return this.get(`/api/workspaces/${slug}/project-states/${stateId}/usage/`);
  }
}
